import { onlineOrderingProductImage } from '@trashlab/api/src/domains/pricing/shared/product-illustration';
import { Box, Flex, Text, Heading } from '@radix-ui/themes';
import StorageImage from '@trashlab/sitesapp/src/organizations/fulltilt/components/shared/storage-image';
import { trpcPricingListSkus } from '@trashlab/api/src/generated/hooks';
import {
  IconRuler3,
  IconPackages,
  IconCalendar,
  IconTruckDelivery,
  IconArrowsExchange,
  IconCheck,
} from '@tabler/icons-react';
import { Button } from '@trashlab/sitesapp/src/organizations/fulltilt/template/components/shared/button';
import currency from 'currency.js';
import { useNavigate } from '@tanstack/react-router';
import { useShoppingCart } from '@trashlab/sitesapp/src/organizations/fulltilt/hooks/use-shopping-cart';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/fulltilt/contexts/use-tenant-context';

// Best Deal Dumpsters org ID
const BEST_DEAL_ORG_ID = 'd74bb19c-004b-479d-adaa-9e846d25dac7';

// Parse cubic yardage from string (e.g., "10", "10YD", "10 Yard" -> 10)
const parseCubicYardage = (cubicYardage: string | number | null | undefined): number | null => {
  if (!cubicYardage) return null;
  const str = String(cubicYardage);
  const match = str.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

// Best Deal specific dumpster information by cubic yardage
const getBestDealDumpsterInfo = (cubicYardage: string | number | null | undefined) => {
  const yards = parseCubicYardage(cubicYardage);
  if (!yards) return null;

  const info: Record<number, { dimensions: string; weight: string; idealFor: string[] }> = {
    10: {
      dimensions: "12' x 8' x 4'",
      weight: 'Up to 2 tons',
      idealFor: [
        'Small garage/basement cleanouts',
        'Small room remodel or roof replacement',
        '~5 pickup truck loads',
      ],
    },
    15: {
      dimensions: "12' x 8' x 5'",
      weight: 'Up to 3 tons',
      idealFor: [
        'Medium garage/basement cleanouts',
        'Medium bathroom remodel',
        '~7.5 pickup truck loads',
      ],
    },
    20: {
      dimensions: "17' x 8' x 6'",
      weight: 'Up to 4 tons',
      idealFor: [
        'Large cleanouts & remodels',
        'Flooring removal for large homes',
        '~10 pickup truck loads',
      ],
    },
    30: {
      dimensions: "22' x 8' x 6'",
      weight: 'Up to 5 tons',
      idealFor: [
        'Extra large cleanouts & home remodels',
        'Commercial roofing jobs',
        '~15 pickup truck loads',
      ],
    },
  };

  return info[yards] || null;
};

const getFrequencyLabel = (frequency?: string, interval?: number) => {
  if (!frequency) return '';

  const intervalStr = interval && interval > 1 ? `${interval} ` : '';

  switch (frequency) {
    case 'DAILY':
      return `${intervalStr}day${interval && interval > 1 ? 's' : ''}`;
    case 'WEEKLY':
      return `${intervalStr}week${interval && interval > 1 ? 's' : ''}`;
    case 'MONTHLY':
      return `${intervalStr}month${interval && interval > 1 ? 's' : ''}`;
    case 'YEARLY':
      return `${intervalStr}year${interval && interval > 1 ? 's' : ''}`;
    default:
      return frequency.toLowerCase();
  }
};

export const DumpsterCard = ({
  sku,
  onClose,
}: {
  sku: NonNullable<typeof trpcPricingListSkus.output>[number];
  onClose?: () => void;
}) => {
  const { website } = useTenantContext();
  const containerPhoto = onlineOrderingProductImage(sku.products[0]);
  const navigate = useNavigate();
  const { isLoading, addToCart } = useShoppingCart();

  const isBestDeal = website?.organization?.id === BEST_DEAL_ORG_ID;
  const bestDealInfo = isBestDeal ? getBestDealDumpsterInfo(sku.product?.cubicYardage) : null;

  const hasSubscription = !!sku.subscriptionSettings;
  const showSubscriptionRate =
    (sku.product?.containerType === 'CART' &&
      website?.showResidentialSubscriptionRates) ||
    (sku.product?.containerType === 'FRONT_LOAD' &&
      website?.showCommercialSubscriptionRates) ||
    (sku.product?.containerType === 'LIQUID_WASTE_CONTAINER' &&
      website?.showPortableToiletSubscriptionRates);

  const handleRentClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (sku.product?.id) {
      await addToCart({
        skuId: sku.id,
        productId: sku.product.id,
        onSuccess: onClose,
      });
    }
  };

  const handleCardClick = () => {
    navigate({ to: `/dumpster-rentals/${sku.id}` });
  };

  return (
    <Box
      className="w-full bg-white rounded-[6px] shadow-lg my-2 flex flex-col cursor-pointer"
      onClick={handleCardClick}
    >
      {containerPhoto && (
        <Box className="relative p-8 rounded-[6px] flex items-center justify-center">
          <StorageImage
            alt="container image"
            bucket="container-product-images"
            fit="contain"
            logoPath={containerPhoto}
          />
        </Box>
      )}

      <Flex direction="column" className="px-6 py-6 flex-1">
        <div>
          <Heading
            as="h3"
            size="5"
            className="text-[18px] text-neutral-900 font-semibold leading-tight"
          >
            {hasSubscription
              ? sku.subscriptionSettings?.name || 'Subscription Service'
              : `${parseCubicYardage(sku.product?.cubicYardage) || sku.product?.cubicYardage} YD Dumpster`}
          </Heading>

          {bestDealInfo ? (
            // Best Deal org: show bullet points with dumpster info
            <Flex direction="column" gap="2" className="mt-4">
              <Flex align="center" gap="2">
                <IconRuler3 size={16} className="text-neutral-500 flex-shrink-0" />
                <Text size="2" className="text-neutral-600">
                  {bestDealInfo.dimensions} · {bestDealInfo.weight}
                </Text>
              </Flex>
              {bestDealInfo.idealFor.map((item, index) => (
                <Flex key={index} align="start" gap="2">
                  <IconCheck size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <Text size="2" className="text-neutral-600">
                    {item}
                  </Text>
                </Flex>
              ))}
            </Flex>
          ) : (
            <Text size="2" className="mt-4 text-neutral-600">
              {hasSubscription
                ? sku.subscriptionSettings?.description ||
                  sku.onlineOrderCopy ||
                  'Recurring service'
                : sku.onlineOrderCopy || ''}
            </Text>
          )}

          <Flex direction="column" className="my-4">
            {hasSubscription && showSubscriptionRate ? (
              <>
                {sku.deliveryPrice && sku.deliveryPrice.chargeFee > 0 && (
                  <Flex align="center" gap="3" className="my-2">
                    <Box className="p-1 rounded-md bg-blue-950/5">
                      <IconTruckDelivery className="text-neutral-600" />
                    </Box>
                    <Text size="2" className="text-neutral-600 ml-3">
                      Delivery:{' '}
                      {currency(sku.deliveryPrice.chargeFee || 0, {
                        fromCents: true,
                      }).format()}
                    </Text>
                  </Flex>
                )}

                {sku.pickupPrice && sku.pickupPrice.chargeFee > 0 && (
                  <Flex align="center" gap="3" className="my-2">
                    <Box className="p-1 rounded-md bg-blue-950/5">
                      <IconArrowsExchange className="text-neutral-600" />
                    </Box>
                    <Text size="2" className="text-neutral-600 ml-3">
                      Pickup:{' '}
                      {currency(sku.pickupPrice.chargeFee || 0, {
                        fromCents: true,
                      }).format()}
                    </Text>
                  </Flex>
                )}
              </>
            ) : (
              <>
                {sku.daysLimit && sku.daysLimit > 0 ? (
                  <Flex align="center" gap="3" className="my-2">
                    <Box className="p-1 rounded-md bg-blue-950/5">
                      <IconCalendar className="text-neutral-600" />
                    </Box>
                    <Text size="2" className="text-neutral-600 ml-3">
                      {sku.daysLimit} day
                      {sku.daysLimit && sku.daysLimit > 1 ? 's' : ''} rental
                    </Text>
                  </Flex>
                ) : null}
              </>
            )}

            {sku.materials.length > 0 && (
              <Flex align="center" gap="3" className="my-2">
                <Box className="p-1 rounded-md bg-blue-950/5">
                  <IconPackages className="text-neutral-600" />
                </Box>
                <Text size="2" className="text-neutral-600 ml-3">
                  {sku.materials.map((m) => m.name).join(', ')}
                </Text>
              </Flex>
            )}
          </Flex>
        </div>

        <Flex
          justify="between"
          align="center"
          gap="4"
          className="mt-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {sku.product?.containerType === 'DUMPSTER' ? (
            <Button
              onClick={handleRentClick}
              withArrow
              loading={isLoading}
              className="flex-1"
            >
              Rent now
            </Button>
          ) : (
            <Button
              href="/contact-us"
              withArrow
              loading={isLoading}
              className="flex-1"
            >
              Book now
            </Button>
          )}

          <Flex direction="column" align="end">
            <Text
              size="2"
              weight="medium"
              className="text-gray-900 uppercase text-right"
            >
              {hasSubscription &&
              showSubscriptionRate &&
              sku.subscriptionItem?.chargeFee
                ? 'Rate'
                : sku.dumpsterPrice?.chargeFee
                ? 'Starting at'
                : ''}
            </Text>

            <Text
              size="4"
              weight="bold"
              className="text-right text-neutral-900 font-bold"
            >
              {hasSubscription &&
              showSubscriptionRate &&
              sku.subscriptionItem?.chargeFee
                ? `${currency(sku.subscriptionItem.chargeFee, {
                    fromCents: true,
                  }).format()}/${getFrequencyLabel(
                    sku.subscriptionSettings?.frequency,
                    sku.subscriptionSettings?.interval,
                  )}`
                : sku.dumpsterPrice?.chargeFee
                ? currency(sku.dumpsterPrice?.chargeFee, {
                    fromCents: true,
                  }).format()
                : ''}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default DumpsterCard;
